import React from "react";
import PropTypes from "prop-types";

// @material-ui/core
import People from "@material-ui/icons/People";
import Search from "@material-ui/icons/Search";
import withStyles from "@material-ui/core/styles/withStyles";
import Avatar from "@material-ui/core/Avatar";
import Chip from "@material-ui/core/Chip";
import LocationOn from "@material-ui/icons/LocationOn";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";

// core components
import GridItem from "components/Grid/GridItem.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import Card from "components/Card/Card.jsx";
import CardIcon from "components/Card/CardIcon.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import Button from "components/CustomButtons/Button.jsx";
import { MEDIA_ROOT } from "config.js";
import {
  prefersDarkModeMediaQuery,
  darkModeStyleBlocks
} from "assets/jss/material-dashboard-react.jsx";

import { getMyUsers } from "../../services/advisorUsers.js";

const traineeCardStyles = {
  cardIconTitle: {
    color: "#3C4858",
    margin: "10px 0 0",
    fontWeight: "300"
  },
  traineeCard: {
    marginBottom: "20px"
  },
  traineeCardBody: {
    padding: "15px"
  },
  traineeHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: "15px",
    flexWrap: "wrap",
    "@media (max-width: 600px)": {
      flexDirection: "column",
      alignItems: "flex-start"
    }
  },
  traineeAvatar: {
    width: "60px",
    height: "60px",
    marginRight: "15px",
    "@media (max-width: 600px)": {
      marginBottom: "10px",
      marginRight: "0"
    }
  },
  traineeInfo: {
    flex: 1,
    minWidth: "0"
  },
  traineeName: {
    fontSize: "16px",
    fontWeight: "500",
    color: "#3C4858",
    marginBottom: "5px",
    wordBreak: "break-word",
    "&:hover": {
      color: "#9c27b0"
    }
  },
  traineeEmail: {
    fontSize: "12px",
    color: "#999",
    marginBottom: "8px",
    wordBreak: "break-word"
  },
  rolesContainer: {
    display: "flex",
    gap: "6px",
    flexWrap: "wrap",
    marginBottom: "10px"
  },
  roleChip: {
    height: "22px",
    fontSize: "10px",
    fontWeight: "500"
  },
  locationContainer: {
    display: "flex",
    alignItems: "center",
    color: "#999",
    fontSize: "13px",
    marginBottom: "12px",
    gap: "4px"
  },
  locationIcon: {
    fontSize: "16px"
  },
  statsContainer: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "10px",
    marginBottom: "15px"
  },
  statItem: {
    padding: "10px",
    backgroundColor: "#f5f5f5",
    borderRadius: "4px"
  },
  statLabel: {
    fontSize: "11px",
    color: "#999",
    marginBottom: "4px"
  },
  statValue: {
    fontSize: "16px",
    fontWeight: "500",
    color: "#3C4858"
  },
  actionsContainer: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap"
  },
  actionButton: {
    flex: 1,
    minWidth: "120px",
    "@media (max-width: 600px)": {
      minWidth: "100%"
    }
  },
  searchContainer: {
    padding: "10px 15px 20px"
  },
  searchField: {
    backgroundColor: "#fff",
    borderRadius: "4px"
  },
  [prefersDarkModeMediaQuery]: darkModeStyleBlocks.myTrainees
};

class MyTrainees extends React.Component {
  state = {
    value: 0,
    users: [],
    searchQuery: ""
  };
  handleChange = (event, value) => {
    this.setState({ value });
  };

  handleChangeIndex = index => {
    this.setState({ value: index });
  };

  componentDidMount() {
    // eslint-disable-next-line react/prop-types
    getMyUsers(this.props.axios).then(data => {
      this.setState({ users: data });
    });
  }

  handleClick = (name, idx) => () => {
    if (name === "dashboard") {
      this.props.history.push(`/trainee-dashboard/${idx}`);
    } else if (name === "chat") {
      this.props.history.push(`/chat/${idx}`);
    } else if (name === "profile") {
      this.props.history.push(`/trainee-profile/${idx}`);
    }
  };

  handleSearchChange = event => {
    this.setState({ searchQuery: event.target.value });
  };

  getRoleColor = role => {
    const roleColors = {
      ADVISOR: "#e91e63",
      BEGINNER: "#00bcd4",
      INTERMEDIATE: "#ff9800",
      ADVANCED: "#4caf50",
      EXPERT: "#f44336"
    };
    return roleColors[role] || "#999";
  };

  filterUsers = users => {
    const { searchQuery } = this.state;
    if (!searchQuery.trim()) return users;

    const query = searchQuery.toLowerCase();
    return users.filter(user => {
      const fullName = `${user.first_name || ""} ${user.last_name || ""}`
        .toLowerCase()
        .trim();
      const username = (user.username || "").toLowerCase();
      const email = (user.email || "").toLowerCase();
      const city = (user.city || "").toLowerCase();
      const country = (user.country || "").toLowerCase();

      return (
        fullName.includes(query) ||
        username.includes(query) ||
        email.includes(query) ||
        city.includes(query) ||
        country.includes(query)
      );
    });
  };

  renderUsers() {
    const { classes } = this.props;
    let users = this.state.users;

    // Sort users by username
    users = users.sort((a, b) => a.username.localeCompare(b.username));

    // Filter users based on search query
    users = this.filterUsers(users);

    return (
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <Card>
            <CardHeader color="rose" icon>
              <CardIcon color="rose">
                <People />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>
                My Trainees ({users.length})
              </h4>
            </CardHeader>
            <CardBody className={classes.searchContainer}>
              <TextField
                fullWidth
                placeholder="Search trainees by name, email, or location..."
                value={this.state.searchQuery}
                onChange={this.handleSearchChange}
                className={classes.searchField}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  )
                }}
              />
            </CardBody>
          </Card>
        </GridItem>

        {users.map((userData, idx) => {
          const userName =
            userData.first_name && userData.last_name
              ? `${userData.first_name} ${userData.last_name}`
              : userData.username;

          const userTrainingProgress =
            userData.preferences && userData.preferences.training_progress
              ? `${Math.round(userData.preferences.training_progress)}%`
              : "--";

          const weeklyLoad =
            userData.preferences && userData.preferences.weekly_training_load
              ? userData.preferences.weekly_training_load
              : "--";

          const location =
            [userData.city, userData.country].filter(Boolean).join(", ") ||
            "Location not specified";

          const avatarSrc = userData.avatar
            ? `${MEDIA_ROOT}${userData.avatar}`
            : null;

          return (
            <GridItem xs={12} sm={6} md={4} key={`trainee-${idx}`}>
              <Card className={classes.traineeCard}>
                <CardBody className={classes.traineeCardBody}>
                  <div className={classes.traineeHeader}>
                    <Avatar
                      src={avatarSrc}
                      alt={userName}
                      className={classes.traineeAvatar}
                    >
                      {!avatarSrc && userName.charAt(0).toUpperCase()}
                    </Avatar>
                    <div className={classes.traineeInfo}>
                      <div
                        className={classes.traineeName}
                        onClick={this.handleClick("profile", userData.username)}
                        style={{ cursor: "pointer" }}
                      >
                        {userName}
                      </div>
                      <div className={classes.traineeEmail}>
                        {userData.email}
                      </div>
                    </div>
                  </div>

                  {userData.roles &&
                    userData.roles.length > 0 && (
                      <div className={classes.rolesContainer}>
                        {userData.roles.map((role, roleIdx) => (
                          <Chip
                            key={`role-${idx}-${roleIdx}`}
                            label={role}
                            className={classes.roleChip}
                            style={{
                              backgroundColor: this.getRoleColor(role),
                              color: "#fff"
                            }}
                          />
                        ))}
                      </div>
                    )}

                  <div className={classes.locationContainer}>
                    <LocationOn className={classes.locationIcon} />
                    <span>{location}</span>
                  </div>

                  <div className={classes.statsContainer}>
                    <div className={classes.statItem}>
                      <div className={classes.statLabel}>Training Progress</div>
                      <div className={classes.statValue}>
                        {userTrainingProgress}
                      </div>
                    </div>
                    <div className={classes.statItem}>
                      <div className={classes.statLabel}>Weekly Load</div>
                      <div className={classes.statValue}>{weeklyLoad}</div>
                    </div>
                  </div>

                  <div className={classes.actionsContainer}>
                    <Button
                      size="sm"
                      color="info"
                      className={classes.actionButton}
                      onClick={this.handleClick("dashboard", userData.username)}
                    >
                      Dashboard
                    </Button>
                    <Button
                      size="sm"
                      color="rose"
                      className={classes.actionButton}
                      onClick={this.handleClick("chat", userData.username)}
                    >
                      Message
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </GridItem>
          );
        })}
      </GridContainer>
    );
  }

  render() {
    return <div>{this.renderUsers()}</div>;
  }
}

MyTrainees.propTypes = {
  classes: PropTypes.object.isRequired,
  history: PropTypes.object,
  axios: PropTypes.func
};

export default withStyles(traineeCardStyles)(MyTrainees);
